import { Box, Text } from "@primer/react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { khoanNopGroupApi } from "../../api/khoanNopGroupApi";
import { saleSetKhoanNopGroupTypeApi } from "../../api/saleSetKhoanNopGroupTypeApi";
import ComboMaHSFilter from "../../components/combo-maHS-filter/ComboMaHSFilter";
import DataGrid from "../../components/data-grid";
import { IDataGridColumn } from "../../components/data-grid/type/IDataGridProps";
import Button from "../../components/ui/button";
import ModalActions from "../../components/ui/modal/ModalActions";
import MyModal from "../../components/ui/modal/MyModal";
import { useCommonContext } from "../../contexts/common";
import { showNotify } from "../../helpers/toast";
import { RootState } from "../../state/reducers";

type SalesetKhoanNopGroupTypeModalProps = {
  onCancel: () => void;
  onSuccess: () => void;
  ts_hocsinh_id?: number;
  ma_hs?: string;
  ho_ten?: string;
  ten_lop?: string;
};

const SalesetKhoanNopGroupTypeModal = (props: SalesetKhoanNopGroupTypeModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [originalRows, setOriginalRows] = useState<any[]>([]);
  const [selectedHocSinh, setSelectedHocSinh] = useState<{ id: number; ma_hs: string; ho_ten: string } | null>(
    props.ts_hocsinh_id ? { id: props.ts_hocsinh_id, ma_hs: props.ma_hs || "", ho_ten: props.ho_ten || "" } : null
  );
  const { translate } = useCommonContext();
  const { nam_hoc: param_nam_hoc } = useSelector((x: RootState) => x.common);
  const { dm_coso_id: param_co_so } = useSelector((x: RootState) => x.common);
  const { sf_khoannop_groups } = useSelector((x: RootState) => x.khoanNopGroup);

  const isNew = !props.ts_hocsinh_id;
  const currentHsId = selectedHocSinh?.id || 0;

  useEffect(() => {
    if (currentHsId > 0) handleReload(currentHsId);
    else setRows([]);
  }, [currentHsId, param_co_so]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReload = async (hsId: number) => {
    setIsLoading(true);
    const res = await khoanNopGroupApi.select_khoannop_by_hs({
      ts_hocsinh_id: hsId,
      dm_coso_id: param_co_so,
      nam_hoc: param_nam_hoc,
    });
    setIsLoading(false);

    const allDichVus = (sf_khoannop_groups || []).filter(
      (x: any) => x.dm_coso_id === param_co_so && (x.is_dichvu || x.is_dichvu_fixed)
    );

    const registeredMap = new Map<number, any>();
    if (res.is_success) {
      (res.data || []).forEach((x: any) => {
        registeredMap.set(x.sf_khoannop_group_id, x);
      });
    }

    const merged = allDichVus.map((dv: any) => {
      const reg = registeredMap.get(dv.id);
      return {
        sf_khoannop_group_id: dv.id,
        ma_khoan_nop_group: dv.ma_khoan_nop_group,
        ten_khoan_nop_group: dv.ten_khoan_nop_group,
        sf_saleset_register_type_id: reg?.sf_saleset_register_type_id ?? 0,
        is_checked: reg ? (reg.sf_saleset_register_type_id > 0) : false,
      };
    });
    setRows(merged);
    setOriginalRows(merged);
  };

  const handleSaveChanges = async () => {
    if (!currentHsId) {
      showNotify({ message: "Vui lòng chọn học sinh", type: "warning" });
      return;
    }
    setIsSaving(true);
    const dataInsert = {
      list_khoannop_group: rows,
      ts_hocsinh_id: currentHsId,
      nam_hoc: param_nam_hoc,
    };
    const res = await saleSetKhoanNopGroupTypeApi.insert_multiple(dataInsert);
    setIsSaving(false);
    if (res.is_success) {
      showNotify({ message: translate("Cập nhật thành công"), type: "success" });
      props.onSuccess();
    } else {
      showNotify({ message: res.message || "Có lỗi, vui lòng kiểm tra lại", type: "error" });
    }
  };

  const kyNops = [
    { id: 10, text: "Theo năm" },
    { id: 5,  text: "Theo kỳ" },
    { id: 13, text: "Theo tháng" },
  ];

  const columns: IDataGridColumn[] = [
    {
      id: "ten_khoan_nop_group",
      dataField: "ten_khoan_nop_group",
      caption: translate("Khoản nộp"),
      width: 220,
      isMainColumn: true,
      cellRender: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{row.ten_khoan_nop_group}</span>
          <span style={{ fontSize: 12, color: "#6e7781" }}>{row.ma_khoan_nop_group}</span>
        </Box>
      ),
    },
    ...kyNops.map((ky) => ({
      id: `ky_${ky.id}`,
      caption: ky.text,
      width: 110,
      align: "center" as const,
      cellRender: (row: any) => {
        const checked = row.sf_saleset_register_type_id === ky.id;
        return (
          <input
            type="checkbox"
            checked={checked}
            onChange={() => {
              setRows((prev) =>
                prev.map((x) =>
                  x.sf_khoannop_group_id !== row.sf_khoannop_group_id
                    ? x
                    : {
                        ...x,
                        sf_saleset_register_type_id: checked ? 0 : ky.id,
                        is_checked: !checked,
                      }
                )
              );
            }}
            style={{ cursor: "pointer", width: 16, height: 16 }}
          />
        );
      },
    })),
  ];

  const subtitle = isNew
    ? (selectedHocSinh ? [selectedHocSinh.ma_hs, selectedHocSinh.ho_ten].filter(Boolean).join(" · ") : "")
    : [props.ma_hs, props.ho_ten, props.ten_lop].filter(Boolean).join(" · ");

  return (
    <MyModal
      isOpen={true}
      onClose={props.onCancel}
      title={translate(isNew ? "Thêm mới đăng ký" : "Cập nhật đăng ký")}
      subtitle={subtitle}
      width="60%"
    >
      <Box sx={{ p: 1 }}>
        {isNew && (
          <Box sx={{ px: 2, pb: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Text sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Học sinh:</Text>
            <ComboMaHSFilter
              defaultValue={{ ma_hs: selectedHocSinh?.ma_hs || "", ts_hocsinh_hoten: selectedHocSinh?.ho_ten || "" }}
              onResultChanged={(value) => {
                setSelectedHocSinh({
                  id: value.ts_hocsinh.id,
                  ma_hs: value.ts_hocsinh.ma_hs,
                  ho_ten: value.ts_hocsinh.ho_ten,
                });
              }}
            />
          </Box>
        )}
        <DataGrid
          dataSource={rows}
          columns={columns}
          keyExpr="sf_khoannop_group_id"
          height={window.innerHeight - (isNew ? 430 : 380)}
          isLoading={isLoading}
          searchEnable={true}
          exportConfig={{ enable: false }}
          filterRow={{ enable: false }}
        />
      </Box>
      <ModalActions>
        <Button variant="default" onClick={props.onCancel}>
          {translate("Đóng")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveChanges}
          isLoading={isSaving}
          disabled={!currentHsId || rows.every((r, i) => r.sf_saleset_register_type_id === originalRows[i]?.sf_saleset_register_type_id)}
        >
          {translate("Cập nhật")}
        </Button>
      </ModalActions>
    </MyModal>
  );
};

export { SalesetKhoanNopGroupTypeModal };
